/**
 * PublicEvidencePage — public, no-auth view of an Event Video clip
 * shared via a tokenised URL.
 *
 * URL: ``/evidence/:token``
 *
 * Renders: branded header → video player → clip metadata → GPS trail.
 * Sensitive fields (IMEI, internal IDs, tenant info) are redacted on
 * the backend before the data ever reaches this component.
 *
 * Reuses ``timezone.js`` helpers so timestamps match the rest of the
 * platform. The trail uses Leaflet (no auth-context needed; standalone).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker as LeafletMarker, Polyline as LeafletPolyline, CircleMarker as LeafletCircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap as useGoogleMap } from '@vis.gl/react-google-maps';
import { AlertTriangle, Loader2, ShieldCheck, Calendar, MapPin, Gauge, Compass, Mountain, Satellite, Clock, Hash, Truck, FileVideo, Download } from 'lucide-react';
import { formatDT, formatTimeOnly, formatWallDT, formatWallTimeOnly, resolveTimezone, formatSpeed, setLocaleSettings } from '../utils/timezone';
import { buildLeafletArrowIcon, buildGoogleArrowIcon } from '../components/media/TrailCursorArrow';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { getHevcSupportSnapshot, hasRenderedVideoFrames, isLikelyHevcVideo } from '../utils/videoCompatibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_MAPS_MAP_ID_NO_POI = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_NO_POI || GOOGLE_MAPS_MAP_ID;

const speedToColor = (speedKmh) => {
  const s = Math.max(0, Number(speedKmh) || 0);
  if (s < 5) return '#3b82f6';
  if (s < 30) return '#22c55e';
  if (s < 60) return '#eab308';
  if (s < 100) return '#f97316';
  return '#ef4444';
};

// Tile picker for Leaflet (used when the operator was on OSM or no
// Google API key is available on this host).
const pickLeafletTile = (mapType, darkModeMap) => {
  if (darkModeMap) {
    return {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      attribution: '&copy; OpenStreetMap, Tiles &copy; Esri',
      subdomains: '',
    };
  }
  switch (mapType) {
    case 'satellite':
    case 'hybrid':
      return { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles &copy; Esri' };
    case 'terrain':
      return { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap, &copy; OpenTopoMap', subdomains: 'abc' };
    case 'osm':
    case 'roadmap':
    default:
      return { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap', subdomains: 'abc' };
  }
};

// Google Maps trail layer — mirrors the EventTrailMap component. Renders
// the polyline + speed-coloured pixel markers via native Google objects,
// plus the time-synced playback cursor (heading-rotated arrow).
const GoogleTrailLayer = ({ points, focusLat, focusLng, cursorPoint, onHover }) => {
  const map = useGoogleMap();
  const polylineRef = useRef(null);
  const markersRef = useRef([]);
  const cursorRef = useRef(null);


  useEffect(() => {
    if (!map || typeof window === 'undefined' || !window.google) return;
    if (!points || points.length === 0) {
      if (focusLat != null && focusLng != null) {
        map.setCenter({ lat: focusLat, lng: focusLng });
        map.setZoom(17);
      }
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (focusLat != null && focusLng != null) bounds.extend({ lat: focusLat, lng: focusLng });
    map.fitBounds(bounds, 30);
  }, [map, points, focusLat, focusLng]);

  useEffect(() => {
    if (!map || typeof window === 'undefined' || !window.google) return;
    if (polylineRef.current) { polylineRef.current.setMap(null); polylineRef.current = null; }
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (!points || points.length === 0) return;
    polylineRef.current = new window.google.maps.Polyline({
      path: points.map(p => ({ lat: p.lat, lng: p.lng })),
      geodesic: false,
      strokeColor: '#60a5fa',
      strokeOpacity: 0.55,
      strokeWeight: 3,
      map,
      zIndex: 5,
    });
    markersRef.current = points.map((p) => {
      const marker = new window.google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 4,
          fillColor: speedToColor(p.speed),
          fillOpacity: 0.95,
          strokeWeight: 0,
        },
        clickable: true,
        zIndex: 6,
      });
      if (onHover) {
        marker.addListener('mouseover', () => onHover(p));
        marker.addListener('mouseout', () => onHover(null));
      }
      return marker;
    });
    return () => {
      if (polylineRef.current) polylineRef.current.setMap(null);
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
    };
  }, [map, points, onHover]);

  // Time-synced playback cursor (heading-rotated arrow). Hover is wired
  // on the cursor itself because at a complete stop the arrow sits on top
  // of an underlying small dot and would otherwise eat its hover event.
  useEffect(() => {
    if (!map || typeof window === 'undefined' || !window.google) return;
    if (cursorRef.current) { cursorRef.current.setMap(null); cursorRef.current = null; }
    if (!cursorPoint) return;
    const icon = buildGoogleArrowIcon({
      heading: cursorPoint.heading || 0,
      color: speedToColor(cursorPoint.speed),
    });
    if (!icon) return;
    cursorRef.current = new window.google.maps.Marker({
      position: { lat: cursorPoint.lat, lng: cursorPoint.lng },
      map,
      icon,
      clickable: true,
      zIndex: 9999,
    });
    if (onHover) {
      cursorRef.current.addListener('mouseover', () => onHover(cursorPoint));
      cursorRef.current.addListener('mouseout', () => onHover(null));
    }
    return () => {
      if (cursorRef.current) cursorRef.current.setMap(null);
    };
  }, [map, cursorPoint, onHover]);

  return null;
};

// Google AdvancedMarker content for the [E] shield — mirrors EventTrailMap.
// Pulse animation removed: its hit-area blocked clicks on nearby data points.
const GoogleEventShield = ({ severity = 'warning', label = 'E' }) => {
  const shieldColor = severity === 'critical' ? '#dc2626' : severity === 'info' ? '#2563eb' : '#f59e0b';
  const safeLabel = String(label || 'E').slice(0, 3);
  return (
    <div style={{ width: 0, height: 0, position: 'relative' }}>
      <div style={{ position: 'absolute', transform: 'translate(-50%, -100%)', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.4))' }}>
        <svg width="30" height="30" viewBox="0 0 514 512" xmlns="http://www.w3.org/2000/svg">
          <path fill={shieldColor} d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z" />
        </svg>
        <div style={{ position: 'absolute', left: '50%', top: '38%', transform: 'translate(-50%, -50%)', font: '700 9px/1 Arial,sans-serif', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,.65)', maxWidth: 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{safeLabel}</div>
      </div>
    </div>
  );
};

const buildLeafletEventIcon = (severity = 'warning', label = 'E') => {
  const shieldColor = severity === 'critical' ? '#dc2626' : severity === 'info' ? '#2563eb' : '#f59e0b';
  const safeLabel = String(label || 'E').slice(0, 3).replace(/[<>"'&]/g, '');
  const testId = safeLabel === 'E' ? 'public-evidence-event-marker' : 'public-evidence-exact-event-marker';
  return L.divIcon({
    className: '',
    html: `<div style="width:0;height:0;overflow:visible;cursor:pointer" data-testid="${testId}">
        <div style="position:absolute;transform:translate(-50%,-100%);filter:drop-shadow(0 2px 6px rgba(0,0,0,.4))">
          <svg width="30" height="30" viewBox="0 0 514 512" xmlns="http://www.w3.org/2000/svg">
            <path fill="${shieldColor}" d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/>
          </svg>
          <div style="position:absolute;left:50%;top:38%;transform:translate(-50%,-50%);font:700 9px/1 Arial,sans-serif;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,.65);max-width:24px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${safeLabel}</div>
        </div>
      </div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
};

const pointTime = (point) => point?.route_timestamp || point?.position_timestamp || point?.timestamp || point?.server_timestamp;

const pointEventCodes = (point) => {
  const codes = new Set();
  const io = point?.io_elements || {};
  const nested = io?.elements || {};
  [point?.event_code, point?.event_type, point?.event_id, io?.event_id, io?.event_code_ext, nested?.event_code_ext]
    .filter(v => v !== undefined && v !== null && v !== '' && String(v) !== '0')
    .forEach(v => codes.add(String(v)));
  return codes;
};

const videoEventCodeSet = (video) => {
  const codes = new Set();
  (Array.isArray(video?.event_codes) ? video.event_codes : []).filter(Boolean).forEach(code => codes.add(String(code)));
  if (video?.event_type && String(video.event_type) !== '0') codes.add(String(video.event_type));
  return codes;
};

const eventLabelForPoint = (point, video) => {
  const pointCodes = pointEventCodes(point);
  const labels = Array.isArray(video?.event_labels) ? video.event_labels : [];
  const codes = Array.isArray(video?.event_codes) ? video.event_codes.map(String) : [];
  for (const code of pointCodes) {
    const idx = codes.indexOf(String(code));
    if (idx >= 0 && labels[idx]) return labels[idx];
  }
  return video?.event_label_display || video?.event_label || 'Event';
};

const FitBoundsToTrail = ({ points, focusLat, focusLng }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) {
      if (focusLat != null && focusLng != null) map.setView([focusLat, focusLng], 17);
      return;
    }
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    if (focusLat != null && focusLng != null) bounds.extend([focusLat, focusLng]);
    map.fitBounds(bounds, { padding: [25, 25], maxZoom: 18 });
  }, [points, focusLat, focusLng, map]);
  return null;
};

const formatBytes = (bytes) => {
  if (!bytes) return '-';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const expiryHuman = (iso) => {
  if (!iso) return 'never';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 1) return `in ${days} days`;
  if (days === 1) return `in 1 day`;
  if (hours > 1) return `in ${hours} hours`;
  return 'in under 1 hour';
};

export default function PublicEvidencePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [playUrl, setPlayUrl] = useState(null);
  const [playLoading, setPlayLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [compatibilityIssue, setCompatibilityIssue] = useState(null);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [compatibilityStatus, setCompatibilityStatus] = useState(null);
  const [compatibilityProgress, setCompatibilityProgress] = useState(null);
  const [hevcSupport, setHevcSupport] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [hoverPoint, setHoverPoint] = useState(null);
  const videoRef = useRef(null);

  // Subscribe to the <video> element's playback position so the trail map
  // can render a heading-rotated arrow at the GPS point that matches each
  // frame. Same time-sync mechanism the operator sees in the in-app modal.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const onTime = () => setCurrentVideoTime(el.currentTime || 0);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('seeked', onTime);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('seeked', onTime);
    };
  }, [playUrl]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${BACKEND_URL}/api/public/evidence/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) {
          let detail = 'Unable to load evidence';
          try { detail = (await res.json())?.detail || detail; } catch { /* noop */ }
          throw new Error(detail);
        }
        return res.json();
      })
      .then(payload => {
        if (cancelled) return;
        // Reflect the sharing account's locale (units + date/time format + timezone) on this public link
        if (payload?.units) setLocaleSettings({ distanceUnit: payload.units.distance_unit, speedUnit: payload.units.speed_unit });
        setLocaleSettings({ dateFormat: payload?.date_format, timeFormat: payload?.time_format, timezone: payload?.account_timezone });
        setData(payload);
      })
      .catch(err => { if (!cancelled) setError(err?.message || 'Unable to load evidence'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Fetch the S3 signed video URL after metadata loads
  useEffect(() => {
    if (!data?.video?.id) return;
    let cancelled = false;
    setPlayLoading(true);
    fetch(`${BACKEND_URL}/api/public/evidence/${encodeURIComponent(token)}/play-url`)
      .then(async (res) => {
        if (!res.ok) {
          let detail = 'Video not yet available';
          try { detail = (await res.json())?.detail || detail; } catch { /* noop */ }
          throw new Error(detail);
        }
        return res.json();
      })
      .then(payload => {
        if (!cancelled && payload?.url) {
          setPlayUrl(payload.url);
          setData(prev => prev ? { ...prev, video: { ...prev.video, ...payload } } : prev);
        }
      })
      .catch(err => { if (!cancelled) toastInPage(err?.message || 'Video not yet available'); })
      .finally(() => { if (!cancelled) setPlayLoading(false); });
    return () => { cancelled = true; };
  }, [data?.video?.id, token]);



  useEffect(() => {
    let cancelled = false;
    getHevcSupportSnapshot().then(snapshot => {
      if (!cancelled) setHevcSupport(snapshot);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!playUrl || !isLikelyHevcVideo(data?.video) || hevcSupport?.supported !== false) return;
    setCompatibilityIssue({
      title: 'This laptop may not support H.265 video',
      message: 'Create a compatible H.264 version for this device.',
    });
  }, [playUrl, data?.video, hevcSupport]);


  const pollCompatibleVersion = async (method = 'GET') => {
    try {
      setCompatibilityLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/public/evidence/${encodeURIComponent(token)}/compatible-version`, { method });
      if (!res.ok) {
        let detail = 'Compatible version is not available';
        try { detail = (await res.json())?.detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const payload = await res.json();
      if (payload?.url) {
        setPlayUrl(payload.url);
        setCompatibilityIssue(null);
        setCompatibilityStatus('Compatible H.264 version ready');
        setCompatibilityProgress(payload?.h264_fallback_progress || { phase: 'ready', percent: 100, estimated_seconds_remaining: 0 });
        setData(prev => prev ? { ...prev, video: { ...prev.video, ...payload } } : prev);
        return;
      }
      const status = payload?.fallback_status || payload?.status;
      const message = payload?.message || 'Preparing compatible H.264 version';
      setCompatibilityStatus(message);
      setCompatibilityProgress(payload?.h264_fallback_progress || null);
      if (['queued', 'converting', 'waiting_for_playback_derivative'].includes(status)) {
        setTimeout(() => pollCompatibleVersion('GET'), Math.max(3, Number(payload?.retry_after || 5)) * 1000);
        return;
      }
      throw new Error(message);
    } catch (err) {
      setCompatibilityIssue({ title: 'Compatible playback failed', message: err?.message || 'Unable to prepare compatible video' });
      toastInPage(err?.message || 'Unable to prepare compatible video');
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const startCompatiblePlayback = () => {
    setCompatibilityIssue(null);
    setCompatibilityStatus('Preparing compatible H.264 version');
    setCompatibilityProgress({ phase: 'starting', percent: 1 });
    pollCompatibleVersion('POST');
  };
  const isCompatibilityBusy = compatibilityLoading || ['queued', 'starting', 'downloading', 'video', 'audio', 'uploading'].includes(compatibilityProgress?.phase);

  useEffect(() => {
    if (!playUrl || !isVideoPlaying || compatibilityIssue) return undefined;
    const timer = setInterval(() => {
      const el = videoRef.current;
      if (el && Number(el.currentTime || 0) > 2 && !hasRenderedVideoFrames(el)) {
        setCompatibilityIssue({
          title: 'Video frames are not rendering on this device',
          message: 'Audio can play while the laptop cannot decode the video stream. Create a compatible H.264 version.',
        });
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [playUrl, isVideoPlaying, compatibilityIssue]);

  const focusLat = data?.video?.latitude != null ? Number(data.video.latitude) : null;
  const focusLng = data?.video?.longitude != null ? Number(data.video.longitude) : null;
  const eventSeverity = useMemo(() => {
    const t = (data?.video?.event_type || data?.video?.event_label || '').toLowerCase();
    if (t.includes('impact') || t.includes('crash') || t.includes('collision') || t.includes('sos')) return 'critical';
    if (t.includes('info') || t.includes('record')) return 'info';
    return 'warning';
  }, [data]);
  const tz = data?.video ? resolveTimezone({ device: data.video }) : undefined;

  // Find the trail point closest to the video's current playback instant
  // (UTC). The clip starts at ``media_start_time_utc``; ``currentVideoTime``
  // is seconds into the clip, so target UTC = start + currentVideoTime.
  // Wrapped in useMemo so the array identity is stable (the `|| []` fallback would
  // otherwise create a new array every render and churn the memos below).
  const trailPoints = useMemo(() => data?.trail?.points || [], [data?.trail?.points]);
  const cursorPoint = useMemo(() => {
    if (!trailPoints.length) return null;
    const startIso = data?.video?.media_start_time_utc || data?.video?.event_time;
    if (!startIso) return null;
    const startMs = new Date(startIso).getTime();
    if (!Number.isFinite(startMs)) return null;
    const targetMs = startMs + (currentVideoTime * 1000);
    // Binary search closest by route/position timestamp; trail is sorted ascending by GPS position time.
    let lo = 0, hi = trailPoints.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const midMs = new Date(pointTime(trailPoints[mid])).getTime();
      if (midMs < targetMs) lo = mid + 1; else hi = mid;
    }
    const a = trailPoints[Math.max(0, lo - 1)];
    const b = trailPoints[lo];
    const aMs = new Date(pointTime(a)).getTime();
    const bMs = new Date(pointTime(b)).getTime();
    return Math.abs(aMs - targetMs) <= Math.abs(bMs - targetMs) ? a : b;
  }, [trailPoints, currentVideoTime, data?.video?.media_start_time_utc, data?.video?.event_time]);

  const eventTrailMarkers = useMemo(() => {
    const video = data?.video;
    if (!trailPoints.length || !video) return [];
    const startIso = video.media_start_time_utc || video.event_time || video.start_time;
    if (!startIso) return [];
    const startMs = new Date(startIso).getTime();
    const endMs = video.media_end_time_utc
      ? new Date(video.media_end_time_utc).getTime()
      : video.end_time
        ? new Date(video.end_time).getTime()
        : Number.isFinite(startMs) && video.duration_seconds
          ? startMs + Number(video.duration_seconds) * 1000
          : startMs;
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return [];
    const wantedCodes = videoEventCodeSet(video);
    const markers = [];
    const seen = new Set();
    for (const point of trailPoints) {
      const t = new Date(pointTime(point)).getTime();
      if (!Number.isFinite(t) || t < startMs || t > endMs) continue;
      const codes = pointEventCodes(point);
      if (wantedCodes.size > 0 && ![...codes].some(code => wantedCodes.has(String(code)))) continue;
      if (codes.size === 0 && wantedCodes.size > 0) continue;
      const key = `${pointTime(point)}-${point.lat}-${point.lng}-${[...codes].join(',')}`;
      if (seen.has(key)) continue;
      seen.add(key);
      markers.push({
        ...point,
        event_label: eventLabelForPoint(point, video),
        event_codes: [...codes],
        marker_label: [...codes][0] || 'E',
      });
    }
    return markers;
  }, [trailPoints, data?.video]);

  const fallbackEventIcon = useMemo(() => buildLeafletEventIcon(eventSeverity, 'E'), [eventSeverity]);

  // Operator's map preferences baked into the share link. Defaults match
  // the previous always-OSM-dark behaviour for older links that were
  // created before this field existed on the backend.
  const mapPrefs = data?.map_preferences || { map_type: 'osm', dark_mode_map: true, show_poi: true };
  const useGoogleOnPublic = Boolean(GOOGLE_MAPS_API_KEY) && mapPrefs.map_type && mapPrefs.map_type !== 'osm';
  const leafletTileCfg = useMemo(() => pickLeafletTile(mapPrefs.map_type, mapPrefs.dark_mode_map), [mapPrefs.map_type, mapPrefs.dark_mode_map]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center" data-testid="public-evidence-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        <span className="ml-3">Loading evidence…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center p-8" data-testid="public-evidence-error">
        <div className="max-w-md text-center">
          <ShieldCheck className="w-12 h-12 mx-auto text-slate-500 mb-3" />
          <h1 className="text-xl font-semibold text-slate-100">Evidence link unavailable</h1>
          <p className="text-sm text-slate-400 mt-2">{error}</p>
          <p className="text-xs text-slate-600 mt-6">If you believe this link should still work, please contact the sender.</p>
        </div>
      </div>
    );
  }

  const v = data?.video || {};
  const link = data?.link || {};
  const brand = data?.brand || {};
  const trail = data?.trail?.points || [];
  const isWall = v.event_time_is_wall_clock;
  const isRemoteDownload = v.event_type === 'remote_download';

  // Operator-opt-in download of the original clip. Hits the gated public
  // endpoint, then triggers a browser download via the presigned URL (which
  // carries a Content-Disposition: attachment header). Every call is logged
  // server-side (timestamp + IP) for chain-of-custody.
  const handleDownloadOriginal = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/evidence/${encodeURIComponent(token)}/download-url`);
      if (!res.ok) {
        let detail = 'Download is not available for this link';
        try { detail = (await res.json())?.detail || detail; } catch { /* noop */ }
        throw new Error(detail);
      }
      const payload = await res.json();
      if (payload?.url) {
        const a = document.createElement('a');
        a.href = payload.url;
        a.download = payload.filename || '';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      toastInPage(err?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };
  const eventTime = v.event_time || v.start_time || v.media_start_time_utc;
  const deviceName = v.device_name || 'Unknown Vehicle';
  const eventMetaLabel = v.has_multiple_events ? 'Events' : 'Event';
  const overlayStart = v.event_time || v.start_time || v.created_at || v.media_start_time_utc;
  const overlayEnd = v.end_time;
  const overlayTitle = v.event_label_display || v.event_label || v.event_type || 'Video';
  const channelName = v.channel_label || v.channel_name || (v.channel != null ? `CH${v.channel}` : 'Channel');
  const channelNumber = v.channel != null ? `CH${v.channel}` : null;
  const eventCodeBadge = Array.isArray(v.event_codes) && v.event_codes.length > 0
    ? v.event_codes.join(', ')
    : (v.event_type || 'Video');
  const overlayTime = overlayStart
    ? `${isWall ? formatWallDT(overlayStart, { seconds: true }) : formatDT(overlayStart, { device: v, seconds: true })}${overlayEnd ? ` → ${isWall ? formatWallTimeOnly(overlayEnd, { seconds: true }) : formatTimeOnly(overlayEnd, { device: v, seconds: true })}` : ''}`
    : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="public-evidence-page">
      {/* Branded header */}
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {brand.logo_url && (
            <img src={brand.logo_url} alt={brand.name} className="h-7 w-auto" />
          )}
          <div>
            <div className="text-sm font-semibold text-slate-100">{brand.name || 'EnsueLIVETRAK'} — Event Evidence</div>
            <div className="text-[11px] text-slate-500">Shared securely · expires {expiryHuman(link.expires_at)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <span>Tokenised view · {link.view_count || 0} {Number(link.view_count) === 1 ? 'view' : 'views'}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6">
        {link.note && (
          <div className="mb-4 bg-slate-900 border-l-4 border-blue-500 px-3 py-2 text-sm text-slate-300" data-testid="public-evidence-note">
            <span className="text-slate-500 text-xs mr-2">Note from sender:</span>
            {link.note}
          </div>
        )}

        {/* Video player */}
        <div className="bg-black rounded-md overflow-hidden border border-slate-800" data-testid="public-evidence-video-container">
          <div className="relative aspect-video">
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4 pr-12 pointer-events-none select-text" data-testid="public-evidence-video-overlay">
              <div className="pointer-events-auto">
                <h3 className="text-white font-semibold text-lg truncate" data-testid="public-evidence-video-overlay-title">{overlayTitle}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={isRemoteDownload ? 'bg-emerald-600 text-white text-xs' : 'bg-blue-600 text-white text-xs'} data-testid="public-evidence-video-overlay-channel-name">
                    {channelName}
                  </Badge>
                  {channelNumber && channelName !== channelNumber && (
                    <Badge variant="outline" className="text-white/70 border-white/30 text-[10px]" data-testid="public-evidence-video-overlay-channel-number">
                      {channelNumber}
                    </Badge>
                  )}
                  <Badge className="bg-slate-600 text-white text-xs" data-testid="public-evidence-video-overlay-event-codes">
                    {isRemoteDownload ? 'Playback' : eventCodeBadge}
                  </Badge>
                  {v.has_multiple_events && (
                    <Badge className="bg-amber-600 text-white text-xs" data-testid="public-evidence-video-overlay-multiple-events-badge">
                      Multiple Events
                    </Badge>
                  )}
                  {deviceName && (
                    <span className="text-white/70 text-sm truncate" data-testid="public-evidence-video-overlay-device-name">{deviceName}</span>
                  )}
                  {overlayTime && (
                    <span
                      className="text-white/60 text-xs truncate"
                      title={tz ? `Timezone: ${tz}` : ''}
                      data-testid="public-evidence-video-overlay-time"
                    >
                      {overlayTime}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {playLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              </div>
            )}
            {playUrl ? (
              <video
                ref={videoRef}
                src={playUrl}
                controls
                playsInline
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                onPlay={() => setIsVideoPlaying(true)}
                onPause={() => setIsVideoPlaying(false)}
                onEnded={() => setIsVideoPlaying(false)}
                onError={() => setCompatibilityIssue({ title: 'This browser cannot decode the video', message: 'Create a compatible H.264 version for this device.' })}
                className="w-full h-full object-contain"
                data-testid="public-evidence-video"
              />
            ) : !playLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                Video file is not available
              </div>
            )}
            {compatibilityIssue && playUrl && (
              <div className="absolute inset-0 bg-black/82 backdrop-blur-sm flex items-center justify-center p-6" data-testid="public-evidence-compatibility-warning">
                <div className="max-w-md text-center text-white">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-300" />
                  <h4 className="font-semibold text-lg" data-testid="public-evidence-compatibility-warning-title">{compatibilityIssue.title}</h4>
                  <p className="text-sm text-white/70 mt-2" data-testid="public-evidence-compatibility-warning-message">{compatibilityIssue.message}</p>
                  {compatibilityStatus && <p className="text-xs text-blue-200 mt-3" data-testid="public-evidence-compatibility-status">{compatibilityStatus}</p>}
                  <CompatibilityProgress progress={compatibilityProgress} status={compatibilityStatus} />
                  {!isCompatibilityBusy && (
                    <Button
                      onClick={startCompatiblePlayback}
                      className="mt-4 bg-amber-500 hover:bg-amber-400 text-black"
                      data-testid="public-evidence-create-compatible-version-button"
                    >
                      <FileVideo className="w-4 h-4 mr-2" />
                      Create compatible version
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Metadata strip */}
        <div className="bg-slate-900 border border-slate-800 border-t-0 rounded-b-md px-4 py-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <MetaItem icon={Truck} label="Vehicle" value={deviceName} testid="public-evidence-meta-device-name" />
            <MetaItem icon={Clock} label="Time" value={isRemoteDownload ? (overlayTime || '-') : (eventTime ? (isWall ? formatWallDT(eventTime, { seconds: true }) : formatDT(eventTime, { device: v, seconds: true })) : '-')} testid="public-evidence-meta-time" />
            <MetaItem icon={Hash} label={eventMetaLabel} value={v.event_label_display || v.event_label || v.event_type || '-'} testid="public-evidence-meta-event" />
            {!isRemoteDownload && (
              <>
                <MetaItem icon={Gauge} label="Speed" value={v.speed != null ? formatSpeed(v.speed) : '-'} testid="public-evidence-meta-speed" />
                <MetaItem icon={Compass} label="Heading" value={v.heading != null ? `${Math.round(v.heading)}°` : '-'} testid="public-evidence-meta-heading" />
                <MetaItem icon={MapPin} label="Location" value={(focusLat != null && focusLng != null) ? `${focusLat.toFixed(5)}, ${focusLng.toFixed(5)}` : '-'} link={(focusLat != null && focusLng != null) ? `https://www.google.com/maps?q=${focusLat},${focusLng}` : null} testid="public-evidence-meta-location" />
                <MetaItem icon={Mountain} label="Altitude" value={v.altitude != null ? `${v.altitude} m` : '-'} testid="public-evidence-meta-altitude" />
                <MetaItem icon={Satellite} label="Satellites" value={v.satellites != null ? v.satellites : '-'} testid="public-evidence-meta-satellites" />
              </>
            )}
            <MetaItem icon={Calendar} label="Channel" value={v.channel_name || (v.channel != null ? `Ch ${v.channel}` : '-')} testid="public-evidence-meta-channel" />
            {isRemoteDownload && v.file_size_display && (
              <MetaItem icon={FileVideo} label="Size" value={v.file_size_display} testid="public-evidence-meta-size" />
            )}
          </div>
          {link.allow_download && (
            <div className="mt-3 pt-3 border-t border-slate-800 flex justify-end">
              <Button
                onClick={handleDownloadOriginal}
                disabled={downloading}
                className="bg-emerald-600 hover:bg-emerald-500 text-white h-9 text-xs"
                data-testid="public-evidence-download-original"
              >
                {downloading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-2" />}
                Download original clip
              </Button>
            </div>
          )}
        </div>

        {/* Trail map */}
        {focusLat != null && focusLng != null && (
          <div className="mt-4 bg-slate-900 border border-slate-800 rounded-md overflow-hidden" data-testid="public-evidence-trail">
            <div className="px-4 py-2 border-b border-slate-800 flex items-center justify-between">
              <div className="text-sm font-medium text-slate-200 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400" /> GPS Trail (±{data?.trail?.window_minutes ?? 3} min)
              </div>
              {trail.length > 0 && <div className="text-xs text-slate-400">{trail.length} GPS points</div>}
            </div>
            <div className="h-[460px] relative">
              {useGoogleOnPublic ? (
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                  <GoogleMap
                    defaultCenter={{ lat: focusLat, lng: focusLng }}
                    defaultZoom={17}
                    mapId={mapPrefs.show_poi !== false ? GOOGLE_MAPS_MAP_ID : GOOGLE_MAPS_MAP_ID_NO_POI}
                    colorScheme={mapPrefs.dark_mode_map ? 'DARK' : 'LIGHT'}
                    mapTypeId={mapPrefs.map_type}
                    style={{ width: '100%', height: '100%' }}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    zoomControl={true}
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                  >
                    <GoogleTrailLayer
                      points={trail}
                      focusLat={focusLat}
                      focusLng={focusLng}
                      cursorPoint={cursorPoint}
                      onHover={setHoverPoint}
                    />
                    {eventTrailMarkers.length > 0 ? eventTrailMarkers.map((marker, idx) => (
                      <AdvancedMarker key={`public-event-marker-${idx}`} position={{ lat: Number(marker.lat), lng: Number(marker.lng) }} zIndex={10000 + idx}>
                        <div onMouseEnter={() => setHoverPoint(marker)} onMouseLeave={() => setHoverPoint(null)} data-testid={`public-evidence-exact-event-marker-${idx}`}>
                          <GoogleEventShield severity={eventSeverity} label={marker.marker_label} />
                        </div>
                      </AdvancedMarker>
                    )) : (
                      <AdvancedMarker position={{ lat: focusLat, lng: focusLng }} zIndex={10000}>
                        <GoogleEventShield severity={eventSeverity} />
                      </AdvancedMarker>
                    )}
                  </GoogleMap>
                </APIProvider>
              ) : (
                <MapContainer
                  center={[focusLat, focusLng]}
                  zoom={17}
                  scrollWheelZoom
                  className="h-full w-full"
                  style={{ background: mapPrefs.dark_mode_map ? '#0b1220' : '#f1f5f9' }}
                >
                  <TileLayer
                    url={leafletTileCfg.url}
                    attribution={leafletTileCfg.attribution}
                    subdomains={leafletTileCfg.subdomains}
                  />
                  <FitBoundsToTrail points={trail} focusLat={focusLat} focusLng={focusLng} />
                  {trail.length >= 2 && (
                    <LeafletPolyline
                      positions={trail.map(p => [p.lat, p.lng])}
                      pathOptions={{ color: '#60a5fa', weight: 3, opacity: 0.45 }}
                    />
                  )}
                  {trail.map((p, i) => {
                    const color = speedToColor(p.speed);
                    return (
                      <LeafletCircleMarker
                        key={`trail-${i}`}
                        center={[p.lat, p.lng]}
                        radius={4}
                        pathOptions={{ color, fillColor: color, fillOpacity: 0.95, weight: 0 }}
                        eventHandlers={{
                          mouseover: () => setHoverPoint(p),
                          mouseout: () => setHoverPoint(null),
                        }}
                      />
                    );
                  })}
                  {cursorPoint && (
                    <LeafletMarker
                      position={[cursorPoint.lat, cursorPoint.lng]}
                      icon={buildLeafletArrowIcon({
                        heading: cursorPoint.heading || 0,
                        color: speedToColor(cursorPoint.speed),
                      })}
                      zIndexOffset={5000}
                      eventHandlers={{
                        mouseover: () => setHoverPoint(cursorPoint),
                        mouseout: () => setHoverPoint(null),
                      }}
                    />
                  )}
                  {eventTrailMarkers.length > 0 ? eventTrailMarkers.map((marker, idx) => (
                    <LeafletMarker
                      key={`public-event-marker-${idx}`}
                      position={[Number(marker.lat), Number(marker.lng)]}
                      icon={buildLeafletEventIcon(eventSeverity, marker.marker_label)}
                      zIndexOffset={9999 + idx}
                      eventHandlers={{
                        mouseover: () => setHoverPoint(marker),
                        mouseout: () => setHoverPoint(null),
                      }}
                    />
                  )) : (
                    <LeafletMarker position={[focusLat, focusLng]} icon={fallbackEventIcon} zIndexOffset={9999} />
                  )}
                </MapContainer>
              )}
              {/* Hover readout — matches the in-app modal style; uses the
                  device's configured timezone so insurance / compliance
                  recipients see local time, never raw UTC. */}
              {hoverPoint && (
                <div
                  className="absolute top-3 left-3 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 pointer-events-none shadow-lg max-w-[280px] z-[1000]"
                  data-testid="public-evidence-trail-hover"
                >
                  <div className="font-medium text-blue-300">
                    {hoverPoint.event_label && <span className="mr-2 text-amber-300">{hoverPoint.event_label}</span>}
                    {formatTimeOnly(pointTime(hoverPoint), { device: v, seconds: true })}
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-[11px] text-slate-300">
                    <span>{formatSpeed(hoverPoint.speed || 0)}</span>
                    <span>{Math.round(hoverPoint.heading || 0)}°</span>
                    {hoverPoint.satellites != null && <span>sat {hoverPoint.satellites}</span>}
                  </div>
                  <div className="mt-0.5 text-[10px] text-slate-500">
                    {Number(hoverPoint.lat).toFixed(5)}, {Number(hoverPoint.lng).toFixed(5)} · {tz || 'UTC'}
                  </div>
                </div>
              )}
              <div className="absolute bottom-3 left-3 bg-slate-900/95 backdrop-blur rounded px-2.5 py-1.5 text-[10px] text-slate-200 border border-slate-700 flex items-center gap-2 z-[1000] shadow-lg">
                <span className="opacity-70">Speed:</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#3b82f6' }} />0</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#22c55e' }} />30</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#eab308' }} />60</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#f97316' }} />90</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#ef4444' }} />100+</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-6 text-center text-[11px] text-slate-600">
          <div>Evidence package generated by {brand.name || 'EnsueLIVETRAK'} · timezone {tz || 'UTC'} · file size {formatBytes(v.file_size)}</div>
          <div className="mt-1">Every view of this link is logged with timestamp and IP address for chain-of-custody compliance.</div>
        </footer>
      </main>
    </div>
  );
}

const MetaItem = ({ icon: Icon, label, value, link, testid }) => (
  <div className="flex items-start gap-2" data-testid={testid}>
    <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-[12px] text-blue-400 hover:underline break-words">{value}</a>
      ) : (
        <div className="text-[12px] text-slate-200 break-words">{value}</div>
      )}
    </div>
  </div>
);

const formatEta = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return null;
  const mins = Math.floor(value / 60);
  const secs = Math.round(value % 60);
  return mins ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${secs}s`;
};

const CompatibilityProgress = ({ progress, status }) => {
  if (!progress && !status) return null;
  const percent = Math.max(0, Math.min(100, Number(progress?.percent ?? 0)));
  const eta = formatEta(progress?.estimated_seconds_remaining);
  const inputMb = progress?.estimated_input_mb;
  const phaseLabel = {
    queued: 'Queued',
    starting: 'Starting',
    downloading: 'Downloading',
    video: 'Converting video',
    audio: 'Adding audio',
    uploading: 'Uploading',
    ready: 'Ready',
    failed: 'Failed',
  }[progress?.phase] || 'Preparing';
  return (
    <div className="mt-4 text-left" data-testid="public-evidence-compatibility-progress">
      <div className="flex items-center justify-between text-xs text-white/75 mb-1">
        <span data-testid="public-evidence-compatibility-progress-phase">{phaseLabel}</span>
        <span data-testid="public-evidence-compatibility-progress-percent">{Math.round(percent)}%</span>
      </div>
      <div className="h-2 bg-white/15 overflow-hidden rounded-sm" data-testid="public-evidence-compatibility-progress-bar">
        <div className="h-full bg-amber-400 transition-[width] duration-500" style={{ width: `${percent}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/55 mt-2">
        {eta && <span data-testid="public-evidence-compatibility-progress-eta">~{eta} remaining</span>}
        {inputMb && <span data-testid="public-evidence-compatibility-progress-size">Source {inputMb} MB</span>}
        {status && <span data-testid="public-evidence-compatibility-progress-status">{status}</span>}
      </div>
    </div>
  );
};

// Lightweight in-page toast fallback (the public page does not load sonner)
function toastInPage(message) {
  try { console.warn('[evidence]', message); } catch { /* noop */ }
}
